import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useContentTier } from '../context/ContentTierContext'
import { getPersonalizedSnapshotHash } from '../utils/personalized-plan-snapshot'
import {
  ANONYMOUS_REFINEMENT_LIMIT,
  readAnonymousRefinementCountFromStorage,
  writeAnonymousRefinementCountToStorage,
} from '../utils/anonymous-refinement-storage'
import type { AnswerDelta } from '../utils/apply-deltas'
import type { ProjectTemplate } from '../types'
import type { Dispatch, SetStateAction } from 'react'

const LIMIT_MESSAGE =
  "You've built your plan! Save it before you go — sign up free to keep customizing."

function redirectSearch(templateSlug: string) {
  return `?redirect_url=${encodeURIComponent(`/diy/${templateSlug}`)}`
}

function showAnonRefinementLimitNotification(templateSlug: string) {
  const search = redirectSearch(templateSlug)
  notifications.show({
    id: 'anon-refinement-limit',
    title: "You've built your plan!",
    color: 'primary',
    message: (
      <Stack gap="sm">
        <Text size="sm">{LIMIT_MESSAGE}</Text>
        <Group gap="sm">
          <Button component="a" size="xs" href={`/sign-up${search}`}>
            Sign up
          </Button>
          <Button component="a" size="xs" variant="default" href={`/sign-in${search}`}>
            Sign in
          </Button>
        </Group>
      </Stack>
    ),
  })
}

interface UseAnonymousRefinementUsageArgs {
  template: ProjectTemplate
  templateSlug: string
  deltas: Array<AnswerDelta>
  isHydrated: boolean
  setAnswers: Dispatch<SetStateAction<Record<string, string>>>
}

/**
 * Tracks **first-time** Q&A answers (empty → non-empty) in `localStorage` (anon) and enforces
 * the five-and-stop rule for **anonymous** users. **Changing** an already-answered question
 * does not increment the count. URL preset bulk apply must still bypass counting — use
 * {@link setAnswers} directly.
 */
export function useAnonymousRefinementUsage({
  template,
  templateSlug,
  deltas,
  isHydrated,
  setAnswers,
}: UseAnonymousRefinementUsageArgs) {
  const { tier, isLoading: tierLoading } = useContentTier()
  const isAnonLimitActive = tier === 'anon' && !tierLoading
  const isAuthed = tier === 'free' || tier === 'premium'

  const [refinementCount, setRefinementCount] = useState(0)
  const countRef = useRef(0)
  /** Signed-in: in-session count of material Q&amp;A customizations (not persisted; not capped). */
  const [authedQaCount, setAuthedQaCount] = useState(0)
  const authedQaCountRef = useRef(0)
  /**
   * React 18 Strict Development invokes the `setAnswers` updater twice; `+1` runs in a microtask
   * deduped with the post-change snapshot hash.
   */
  const lastCountedSnapshotHashRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isHydrated) return
    const c = readAnonymousRefinementCountFromStorage(templateSlug)
    countRef.current = c
    if (tier === 'free' || tier === 'premium') {
      setRefinementCount(0)
    } else {
      setRefinementCount(c)
    }
  }, [isHydrated, templateSlug, tier, tierLoading])

  useEffect(() => {
    lastCountedSnapshotHashRef.current = null
  }, [templateSlug])

  const handleAnswerChange = useCallback(
    (key: string, value: string) => {
      setAnswers((prev) => {
        if ((prev[key] ?? '') === value) {
          return prev
        }
        const next: Record<string, string> = { ...prev, [key]: value }
        if (!isHydrated) {
          return next
        }
        const prevWasEmpty = (prev[key] ?? '').trim() === ''
        const valueIsAnswered = value.trim() !== ''
        /** New customization = first time this question gets a non-empty answer (edits A→B do not count). */
        const isFirstAnswerToQuestion = prevWasEmpty && valueIsAnswered

        const nextHash = getPersonalizedSnapshotHash(
          template,
          next,
          deltas,
          isHydrated
        )
        // Clerk still loading: tier is "anon" but we cannot know auth yet — only block
        // *new* answers at limit (edits still allowed).
        if (tierLoading) {
          if (
            isFirstAnswerToQuestion &&
            countRef.current >= ANONYMOUS_REFINEMENT_LIMIT
          ) {
            queueMicrotask(() => {
              showAnonRefinementLimitNotification(templateSlug)
            })
            return prev
          }
          return next
        }
        if (isAnonLimitActive) {
          if (
            isFirstAnswerToQuestion &&
            countRef.current >= ANONYMOUS_REFINEMENT_LIMIT
          ) {
            queueMicrotask(() => {
              showAnonRefinementLimitNotification(templateSlug)
            })
            return prev
          }
          if (isFirstAnswerToQuestion) {
            queueMicrotask(() => {
              if (nextHash === lastCountedSnapshotHashRef.current) return
              lastCountedSnapshotHashRef.current = nextHash
              if (countRef.current >= ANONYMOUS_REFINEMENT_LIMIT) return
              countRef.current = Math.min(
                ANONYMOUS_REFINEMENT_LIMIT,
                countRef.current + 1
              )
              writeAnonymousRefinementCountToStorage(templateSlug, countRef.current, {
                max: ANONYMOUS_REFINEMENT_LIMIT,
              })
              setRefinementCount(countRef.current)
            })
          }
        } else if (isFirstAnswerToQuestion) {
          queueMicrotask(() => {
            if (nextHash === lastCountedSnapshotHashRef.current) return
            lastCountedSnapshotHashRef.current = nextHash
            authedQaCountRef.current += 1
            setAuthedQaCount(authedQaCountRef.current)
          })
        }
        return next
      })
    },
    [
      deltas,
      isAnonLimitActive,
      isHydrated,
      setAnswers,
      template,
      templateSlug,
      tierLoading,
    ]
  )

  return {
    // Anonymous: storage-backed. Authenticated: in-session (display total). Count = new answers, not edits.
    refinementCount: isAuthed ? authedQaCount : refinementCount,
    isAnonLimitActive,
    handleAnswerChange,
    atRefinementLimit:
      isAnonLimitActive && refinementCount >= ANONYMOUS_REFINEMENT_LIMIT,
  }
}
